import { RouteManagerContext, useIonRouter } from '@ionic/react';
import { useCallback, useContext } from 'react';
import { shellPaths } from '@/core/router/app-paths';

/** Auth routes that can remain mounted in the root outlet after login. */
export const AUTH_STACK_PATHS = [
  '/auth/welcome',
  '/auth',
  '/auth/verify',
  '/auth/register',
  '/auth/complete',
] as const;

type IonRouter = ReturnType<typeof useIonRouter>;

export type RouteManagerApi = {
  findViewItemByPathname: (
    pathname: string,
    outletId?: string,
  ) => unknown | undefined;
  unMountViewItem: (viewItem: unknown) => void;
};

export function isRouteManagerReady(
  routeManager: unknown,
): routeManager is RouteManagerApi {
  return (
    typeof routeManager === 'object' &&
    routeManager !== null &&
    'findViewItemByPathname' in routeManager &&
    'unMountViewItem' in routeManager &&
    typeof (routeManager as RouteManagerApi).findViewItemByPathname ===
      'function' &&
    typeof (routeManager as RouteManagerApi).unMountViewItem === 'function'
  );
}

/** Removes cached auth IonRoute pages so swipe-back cannot return to login. */
export function clearAuthStackViewItems(routeManager: RouteManagerApi): void {
  for (const path of AUTH_STACK_PATHS) {
    let item = routeManager.findViewItemByPathname(path);
    while (item) {
      routeManager.unMountViewItem(item);
      item = routeManager.findViewItemByPathname(path);
    }
  }
}

/**
 * Leave the auth/standalone tree for the authenticated shell.
 *
 * Auth→shell is a hard React tree swap (`AppNavigationTree` mounts `ShellEntry`
 * outside the root `IonRouterOutlet`). This helper only updates the URL and
 * clears leftover auth view items — it must not rely on a root-outlet
 * PageManager enter to reveal `IonTabs`.
 */
export function enterAppShell(
  router: IonRouter,
  routeManager: RouteManagerApi | undefined,
  path: string = shellPaths.home,
): void {
  if (routeManager) {
    clearAuthStackViewItems(routeManager);
  }
  router.push(path, 'root', 'replace', { unmount: true });
}

export function useEnterAppShell() {
  const router = useIonRouter();
  const routeManager = useContext(RouteManagerContext);

  return useCallback(
    (path: string = shellPaths.home) => {
      enterAppShell(
        router,
        isRouteManagerReady(routeManager) ? routeManager : undefined,
        path,
      );
    },
    [router, routeManager],
  );
}
